import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika, PaprikaShadows } from '../../styles/theme';
import { useGroceryBannerState } from './useGroceryBannerState';

/**
 * Week counter banner — the single addition to the week screen.
 *
 * Two jobs, depending on whether the week on screen is the week the grocery
 * list belongs to:
 *
 * - Same week: totals the items left to buy and opens the grocery list on tap.
 *   Green success state when the pantry already covers everything.
 * - Different week: offers to shop for the week on screen. Browsing the planner
 *   never rewrites the list on its own — only this tap does.
 *
 * Hidden when the week on screen has no recipe meals.
 */
export const WeekCounterBanner: React.FC = () => {
  const { recipeMealCount, isOtherWeek, allCovered, label, sublabel, onPress } =
    useGroceryBannerState();

  if (recipeMealCount === 0) return null;

  // The list belongs to another week — offer to switch instead of showing a
  // count that doesn't describe what's on screen.
  if (isOtherWeek) {
    return (
      <Pressable
        style={({ pressed }) => [
          styles.banner,
          styles.bannerOther,
          pressed && styles.bannerPressed,
        ]}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={label}
      >
        <View style={styles.iconSquareOther}>
          <Feather name="shopping-cart" size={19} color={Paprika.accent} />
        </View>
        <View style={styles.textBlock}>
          {/* Two lines: this label is markedly longer than the item count it
              replaces, and it truncates on narrow widths at one line. */}
          <Text style={styles.titleOther} numberOfLines={2}>
            {label}
          </Text>
          {sublabel ? (
            <Text style={styles.subtitleOther} numberOfLines={1}>
              {sublabel}
            </Text>
          ) : null}
        </View>
        <Feather name="chevron-right" size={18} color={Paprika.textSecondary} />
      </Pressable>
    );
  }

  // Nothing left to buy: a full-width banner saying so is a status message with
  // no action behind it, and its copy ("Tout est au garde-manger") claimed the
  // pantry covered the week when the items may simply be assumed staples —
  // the same false claim #625 reported on the grocery screen. The compact
  // summary line and desktop chip still carry the state, and Épicerie in the
  // nav still opens the list, so nothing becomes unreachable.
  if (allCovered) return null;

  return (
    <Pressable
      style={({ pressed }) => [
        styles.banner,
        allCovered ? styles.bannerDone : styles.bannerBuy,
        pressed && styles.bannerPressed,
      ]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <View style={styles.iconSquare}>
        <Feather name={allCovered ? 'check' : 'shopping-cart'} size={19} color="#fff" />
      </View>
      <View style={styles.textBlock}>
        {/* The buy state has no subtitle: `label` now carries "pour vos N
            repas" itself, and the 40px iconSquare keeps the row height, so
            dropping the second line does not shrink the banner. */}
        <Text style={styles.title} numberOfLines={1}>
          {label}
        </Text>
        {sublabel ? (
          <Text style={styles.subtitle} numberOfLines={1}>
            {sublabel}
          </Text>
        ) : null}
      </View>
      <Feather name="chevron-right" size={18} color="rgba(255,255,255,0.75)" />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderRadius: 15,
    paddingVertical: 13,
    paddingHorizontal: 15,
    ...PaprikaShadows.card,
  },
  bannerBuy: {
    backgroundColor: Paprika.accent,
  },
  bannerDone: {
    backgroundColor: Paprika.success,
  },
  // "Shop for this week" — floating white paper rather than a filled accent, so
  // it reads as an offer rather than the active state of the current week.
  bannerOther: {
    backgroundColor: Paprika.surfaceLowest,
  },
  bannerPressed: {
    opacity: 0.9,
  },
  iconSquare: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.16)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconSquareOther: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: Paprika.accentLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textBlock: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
    fontSize: 15,
    lineHeight: 19,
    color: '#fff',
    fontVariant: ['tabular-nums'],
  },
  subtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    color: 'rgba(255,255,255,0.75)',
    marginTop: 1,
  },
  titleOther: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
    fontSize: 15,
    lineHeight: 19,
    color: Paprika.textPrimary,
  },
  subtitleOther: {
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    color: Paprika.textSecondary,
    marginTop: 1,
  },
});

export default WeekCounterBanner;
